import React, { useState, useEffect } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Slider,
  Divider,
} from '@mui/material';
import api from '../../services/api';
import { FEATURE_FLAG_KEYS, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import SettingsIcon from '@mui/icons-material/Settings';
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useSearchSettings } from '../../contexts/SearchSettingsContext';
import { useColorMode } from '../../contexts/ColorModeContext';
import { useHelpContent } from '../../utils/HelpUtils';

interface SettingsMenuProps {
  onAutoPasteChange: (enabled: boolean) => void;
  onToolbarDockMagnifyChange: (enabled: boolean) => void;
  onShowContentIndexInPaneChange: (enabled: boolean) => void;
  onClickToRenameCategoryInTreeChange: (enabled: boolean) => void;
  clickToRenameCategoryInTree: boolean;
  fontSize: number;
  onFontSizeChange: (size: number) => void;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({
  onAutoPasteChange,
  onToolbarDockMagnifyChange,
  onShowContentIndexInPaneChange,
  onClickToRenameCategoryInTreeChange,
  clickToRenameCategoryInTree,
  fontSize,
  onFontSizeChange,
}) => {
  const { settings, updateSettings } = useSearchSettings();
  const { findSimilarityThreshold } = settings;
  const { preference, setPreference } = useColorMode();
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const semanticFindEnabled = isFeatureEnabled(FEATURE_FLAG_KEYS.SEMANTIC_FIND);
  const { getToolbarTooltipTextForState } = useHelpContent();

  const persistFindSearchSettings = async (patch: { findSimilarityThreshold?: number }) => {
    try {
      await api.put('/search/settings', { search: patch });
    } catch (error) {
      console.error('Error updating find search settings:', error);
    }
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [autoPasteEnabled, setAutoPasteEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoPasteEnabled');
    return saved ? JSON.parse(saved) : false;
  });
  const [toolbarDockMagnify, setToolbarDockMagnify] = useState<boolean>(() => {
    const saved = localStorage.getItem('toolbarDockMagnify');
    if (!saved) return false;
    try {
      return Boolean(JSON.parse(saved));
    } catch {
      localStorage.removeItem('toolbarDockMagnify');
      return false;
    }
  });
  const [showContentIndexInPane, setShowContentIndexInPane] = useState<boolean>(() => {
    const saved = localStorage.getItem('showContentIndexInPane');
    if (!saved) return false;
    try {
      return Boolean(JSON.parse(saved));
    } catch {
      localStorage.removeItem('showContentIndexInPane');
      return false;
    }
  });
  const [tooltipFontSize, setTooltipFontSize] = useState<number>(() => {
    const saved = localStorage.getItem('tooltipFontSize');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        // If parsing fails, remove the invalid value and use default
        localStorage.removeItem('tooltipFontSize');
        return 16;
      }
    }
    return 16;
  });

  const clampToolbarIconSize = (value: number) =>
    Math.min(34, Math.max(18, Math.round(value)));

  const [toolbarIconSize, setToolbarIconSize] = useState<number>(() => {
    const saved = localStorage.getItem('toolbarIconSize');
    if (saved) {
      try {
        return clampToolbarIconSize(JSON.parse(saved));
      } catch (error) {
        localStorage.removeItem('toolbarIconSize');
        return 24;
      }
    }
    return 24;
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAutoPasteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setAutoPasteEnabled(newValue);
    localStorage.setItem('autoPasteEnabled', JSON.stringify(newValue));
    onAutoPasteChange(newValue);
  };

  const handleToolbarDockMagnifyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setToolbarDockMagnify(newValue);
    localStorage.setItem('toolbarDockMagnify', JSON.stringify(newValue));
    onToolbarDockMagnifyChange(newValue);
  };

  const handleShowContentIndexInPaneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setShowContentIndexInPane(newValue);
    localStorage.setItem('showContentIndexInPane', JSON.stringify(newValue));
    onShowContentIndexInPaneChange(newValue);
  };

  const handleClickToRenameCategoryInTreeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onClickToRenameCategoryInTreeChange(event.target.checked);
  };

  const handleHelpModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    updateSettings({ ...settings, helpMode: newValue });
  };

  const handleShortTooltipsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    updateSettings({ ...settings, shortTooltips: newValue });
  };

  const handleTooltipFontSizeChange = (event: Event, newValue: number | number[]) => {
    const fontSize = newValue as number;
    setTooltipFontSize(fontSize);
    localStorage.setItem('tooltipFontSize', JSON.stringify(fontSize));
    // Update CSS custom property for tooltips
    document.documentElement.style.setProperty('--tooltip-font-size', `${fontSize}px`);
  };

  // Initialize tooltip font size on component mount
  useEffect(() => {
    document.documentElement.style.setProperty('--tooltip-font-size', `${tooltipFontSize}px`);
  }, [tooltipFontSize]);

  // Initialize tooltip font size on app startup
  useEffect(() => {
    const savedFontSize = localStorage.getItem('tooltipFontSize');
    if (savedFontSize) {
      try {
        const fontSize = JSON.parse(savedFontSize);
        document.documentElement.style.setProperty('--tooltip-font-size', `${fontSize}px`);
      } catch (error) {
        // If parsing fails, remove the invalid value and use default
        localStorage.removeItem('tooltipFontSize');
        document.documentElement.style.setProperty('--tooltip-font-size', '16px');
      }
    } else {
      // Set default if no saved value
      document.documentElement.style.setProperty('--tooltip-font-size', '16px');
    }
  }, []);

  const handleToolbarIconSizeChange = (event: Event, newValue: number | number[]) => {
    const next = clampToolbarIconSize(newValue as number);
    setToolbarIconSize(next);
    localStorage.setItem('toolbarIconSize', JSON.stringify(next));
    document.documentElement.style.setProperty('--app-toolbar-icon-size', `${next}px`);
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--app-toolbar-icon-size', `${toolbarIconSize}px`);
  }, [toolbarIconSize]);

  return (
    <>
      <Tooltip title={getToolbarTooltipTextForState('settings', true)} placement="left">
        <IconButton
          color="inherit"
          onClick={handleClick}
          size="small"
          sx={{ ml: 2 }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: 300,
            maxWidth: 320,
            maxHeight: 'calc(100dvh - 72px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block' }}>
          Appearance
        </Typography>
        <Tooltip title={getToolbarTooltipTextForState('settings-theme-system', true)} placement="left">
          <span>
            <MenuItem onClick={() => setPreference('system')} selected={preference === 'system'} dense>
              <ListItemIcon>
                <BrightnessAutoIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="System" secondary="Match device" />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('settings-theme-light', true)} placement="left">
          <span>
            <MenuItem onClick={() => setPreference('light')} selected={preference === 'light'} dense>
              <ListItemIcon>
                <LightModeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Light" />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('settings-theme-dark', true)} placement="left">
          <span>
            <MenuItem onClick={() => setPreference('dark')} selected={preference === 'dark'} dense>
              <ListItemIcon>
                <DarkModeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Dark" />
            </MenuItem>
          </span>
        </Tooltip>
        <Divider sx={{ my: 1 }} />
        <Tooltip title={getToolbarTooltipTextForState('auto-paste', true)} placement="left">
          <span>
            <MenuItem>
              <ListItemText primary="Auto-paste from clipboard" />
              <Switch
                edge="end"
                checked={autoPasteEnabled}
                onChange={handleAutoPasteChange}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('toolbar-dock-magnify', true)} placement="left">
          <span>
            <MenuItem>
              <ListItemText primary="Dock-style toolbar icons" />
              <Switch
                edge="end"
                checked={toolbarDockMagnify}
                onChange={handleToolbarDockMagnifyChange}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('show-content-index-in-pane', true)} placement="left">
          <span>
            <MenuItem>
              <ListItemText primary="Show content row numbers" />
              <Switch
                edge="end"
                checked={showContentIndexInPane}
                onChange={handleShowContentIndexInPaneChange}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('click-to-rename-category-in-tree', true)} placement="left">
          <span>
            <MenuItem>
              <ListItemText primary="Click category name to rename" />
              <Switch
                edge="end"
                checked={clickToRenameCategoryInTree}
                onChange={handleClickToRenameCategoryInTreeChange}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('help-mode', true)} placement="left">
          <span>
            <MenuItem>
              <ListItemText primary="Detailed help tooltips" />
              <Switch
                edge="end"
                checked={settings.helpMode}
                onChange={handleHelpModeChange}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('short-tooltips', !settings.helpMode)} placement="left">
          <span>
            <MenuItem>
              <ListItemText primary="Short tooltips" />
              <Switch
                edge="end"
                checked={settings.shortTooltips}
                onChange={handleShortTooltipsChange}
                disabled={settings.helpMode}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('tooltip-font-size', true)} placement="left">
          <span>
            <MenuItem sx={{ flexDirection: 'column', alignItems: 'stretch', py: 2 }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Tooltip Font Size: {tooltipFontSize}px
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    A
                  </Typography>
                  <Slider
                    value={tooltipFontSize}
                    onChange={handleTooltipFontSizeChange}
                    min={12}
                    max={24}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ mx: 1, flex: 1 }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '24px', color: 'text.secondary' }}>
                    A
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('toolbar-icon-size', true)} placement="left">
          <span>
            <MenuItem sx={{ flexDirection: 'column', alignItems: 'stretch', py: 2 }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Toolbar icon size: {toolbarIconSize}px
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    S
                  </Typography>
                  <Slider
                    value={toolbarIconSize}
                    onChange={handleToolbarIconSizeChange}
                    min={18}
                    max={34}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ mx: 1, flex: 1 }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '24px', color: 'text.secondary' }}>
                    L
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title="Application Font Size" placement="left">
          <span>
            <MenuItem sx={{ flexDirection: 'column', alignItems: 'stretch', py: 2 }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Application Font Size: {fontSize}px
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    A
                  </Typography>
                  <Slider
                    value={fontSize}
                    onChange={(event, newValue) => onFontSizeChange(newValue as number)}
                    min={10}
                    max={24}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ mx: 1, flex: 1 }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '24px', color: 'text.secondary' }}>
                    A
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          </span>
        </Tooltip>
        {semanticFindEnabled && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 0.5, pb: 0.5, display: 'block' }}>
              Semantic find &amp; matching
            </Typography>
            <Tooltip title={getToolbarTooltipTextForState('settings-find-similarity-threshold', true)} placement="left">
              <span style={{ display: 'block', width: '100%' }}>
                <MenuItem sx={{ flexDirection: 'column', alignItems: 'stretch', py: 2, width: '100%', boxSizing: 'border-box' }}>
                  <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Similarity threshold: {findSimilarityThreshold.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Find (Semantic) and Add matching content (AI)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 0.5, boxSizing: 'border-box' }}>
                      <Slider
                        value={findSimilarityThreshold}
                        onChange={(_, value) => updateSettings({ findSimilarityThreshold: value as number })}
                        onChangeCommitted={(_, value) => {
                          updateSettings({ findSimilarityThreshold: value as number });
                          void persistFindSearchSettings({ findSimilarityThreshold: value as number });
                        }}
                        min={0}
                        max={1}
                        step={0.01}
                        size="small"
                        valueLabelDisplay="off"
                        sx={{ flex: 1, minWidth: 0, mx: 0.5 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Lower = more results; higher = stricter matches
                    </Typography>
                  </Box>
                </MenuItem>
              </span>
            </Tooltip>
          </>
        )}
      </Menu>
    </>
  );
};

export default SettingsMenu; 